import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { FaEdit, FaTrash, FaGripVertical } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import { apiFetch } from "./utils/api";
import SectionVisibility from "./utils/Dolluzcorp_SectionVisibility";
import { useQuill } from "react-quilljs";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "quill/dist/quill.snow.css";
import "./Dolluzcorp_Policies_config.css";

// react-select renders its menu inline by default, so any ancestor with
// overflow:hidden (our form cards) clips it. Portal the menu to <body>.
const SELECT_PORTAL = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

const AUDIENCE_TYPES = [
    { value: "general", label: "General - Everyone (public)" },
    { value: "all_employees", label: "All Employees (logged-in staff)" },
    { value: "department", label: "Department" },
    { value: "job_position", label: "Job Position" },
    { value: "location", label: "Location" },
    { value: "access_level", label: "Access Level" },
    { value: "employee", label: "Employee" },
];
const SCOPED = ["department", "job_position", "location", "access_level", "employee"];
const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
const ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];
const MIN_DESC = 200;

const plainLen = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    return div.innerText.trim().length;
};

const plainText = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    return div.innerText.trim();
};

// What the portal shows on the teaser card: the excerpt you wrote, or a
// trimmed slice of the description for older policies with no excerpt yet.
const cardTeaser = (excerpt, description) => {
    const e = (excerpt || "").trim();
    if (e) return e;
    const t = plainText(description);
    return t.length > 120 ? t.slice(0, 120) + "…" : t;
};

const Dolluzcorp_Policies_config = ({ navSize }) => {
    const navigate = useNavigate();
    const [policies, setPolicies] = useState([]);
    const [title, setTitle] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [description, setDescription] = useState("");
    const [audienceType, setAudienceType] = useState("general");
    const [audienceValue, setAudienceValue] = useState([]);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [previewMode, setPreviewMode] = useState("card");   // "card" | "full"

    const [departments, setDepartments] = useState([]);
    const [jobPositions, setJobPositions] = useState([]);
    const [employees, setEmployees] = useState([]);

    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["clean"],
            ],
        },
    });

    useEffect(() => { verifySession(); fetchPolicies(); fetchDepartments(); fetchJobPositions(); fetchEmployees(); }, []);
    useEffect(() => {
        if (!quill) return;
        const handler = () => setDescription(quill.root.innerHTML);
        quill.on("text-change", handler);
        return () => quill.off("text-change", handler);
    }, [quill]);

    const verifySession = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") navigate("/login");
        } catch (err) { console.error("session check failed:", err); }
    };

    const fetchPolicies = async () => { try { const r = await apiFetch("/api/Dolluzcorp/policies/list"); const d = await r.json(); if (d.success) setPolicies(d.data); } catch (e) { console.error(e); } };
    const fetchDepartments = async () => { try { const r = await apiFetch("/api/Dolluzcorp/departments/list"); const d = await r.json(); if (d.success) setDepartments(d.data); } catch { /* ignore */ } };
    const fetchJobPositions = async () => { try { const r = await apiFetch("/api/Employee_configuration/get-job_position_config"); setJobPositions(await r.json()); } catch { /* ignore */ } };
    const fetchEmployees = async () => { try { const r = await apiFetch("/api/employee/directory"); setEmployees(await r.json()); } catch { /* ignore */ } };

    const audienceOptionsFor = (type) => {
        switch (type) {
            case "department": return departments.map(d => ({ value: String(d.department_id), label: d.department_name }));
            case "job_position": return jobPositions.map(j => ({ value: String(j.job_id), label: j.job_name }));
            case "location": return LOCATIONS.map(l => ({ value: l, label: l }));
            case "access_level": return ACCESS_LEVELS.map(l => ({ value: l, label: l }));
            case "employee": return employees.map(e => ({ value: String(e.emp_id), label: `${e.emp_first_name} ${e.emp_last_name}` }));
            default: return [];
        }
    };

    const audienceBadge = (aType, aVal) => {
        if (aType === "general" || !aType) return "Everyone";
        if (aType === "all_employees") return "All Employees";
        const count = (aVal && aVal !== "ALL") ? aVal.split(",").filter(Boolean).length : 0;
        const label = { department: "Department", job_position: "Job Position", location: "Location", access_level: "Access Level", employee: "Employee" }[aType] || aType;
        return `${label}${count ? ` (${count})` : ""}`;
    };

    const isScoped = SCOPED.includes(audienceType);
    const descLen = plainLen(description);

    const validate = () => {
        if (!title.trim()) return "Title is required.";
        if (isScoped && audienceValue.length === 0) return "Pick at least one value for the selected audience.";
        if (descLen < MIN_DESC) return `Description must be at least ${MIN_DESC} characters (currently ${descLen}).`;
        return null;
    };

    const submitPolicy = async () => {
        if (saving) return;
        const err = validate();
        if (err) { dnAlert.warning("Check the form", err); return; }

        setSaving(true);
        try {
            const body = {
                title: title.trim(),
                excerpt: excerpt.trim(),
                description,
                audience_type: audienceType,
                audience_value: isScoped ? audienceValue.join(",") : "",
            };
            let url = "/api/Dolluzcorp/policies/create";
            if (editId) { body.policy_id = editId; url = "/api/Dolluzcorp/policies/update"; }

            const res = await apiFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
            const data = await res.json();
            if (!data.success) { if (data.devError) console.error("policies save:", data.devError); dnAlert.error("Couldn’t save", data.message || "Something went wrong. Please try again."); return; }
            dnAlert.success("Success", editId ? "Policy updated" : "Policy created");
            resetForm();
            fetchPolicies();
        } catch (e) { dnAlert.error("Error", "Something went wrong"); }
        finally { setSaving(false); }
    };

    const startEdit = (p) => {
        setEditId(p.policy_id);
        setTitle(p.title || "");
        setExcerpt(p.excerpt || "");
        setDescription(p.description || "");
        if (quill) quill.root.innerHTML = p.description || "";
        setAudienceType(p.audience_type || "general");
        setAudienceValue((p.audience_value && p.audience_value !== "ALL") ? p.audience_value.split(",").filter(Boolean) : []);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const resetForm = () => {
        setEditId(null); setTitle(""); setExcerpt(""); setDescription("");
        setAudienceType("general"); setAudienceValue([]);
        if (quill) quill.setText("");
    };

    const deletePolicy = async (policy_id) => {
        const ok = await dnAlert.confirm({ title: "Delete policy?", message: "This cannot be undone.", danger: true, confirmLabel: "Delete" });
        if (!ok) return;
        const res = await apiFetch("/api/Dolluzcorp/policies/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ policy_id }) });
        const data = await res.json();
        if (!data.success) { if (data.devError) console.error("policies delete:", data.devError); dnAlert.error("Couldn’t delete", data.message || "Something went wrong. Please try again."); return; }
        dnAlert.success("Deleted", "Policy removed");
        if (editId === policy_id) resetForm();
        fetchPolicies();
    };

    const handleReorder = async (result) => {
        if (!result.destination) return;
        const items = Array.from(policies);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);
        setPolicies(items);
        await apiFetch("/api/Dolluzcorp/policies/reorder", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orderedIds: items.map(p => p.policy_id) }),
        });
    };

    return (
        <div className="policies-container">
            <div className={`policies-dashboard ${navSize}`}>
                <div className="pol-header-wrapper">
                    <h2>
                        <img src={logo_eagle} alt="dAdmin Logo" className="pol-header-logo" />
                        Policies Configuration
                    </h2>
                    <p className="pol-subtitle">
                        Publish company policies to the Inside D portal. Choose who each policy is for, write it in rich text,
                        preview the portal card live, and drag to set the order they appear in.
                    </p>
                </div>

                <div className="policies-dashboard-content">
                    <SectionVisibility section="policies" label="Policies" />

                    <div className="pol-grid">
                        {/* ── LEFT: compose ── */}
                        <div className="pol-compose">
                            <div className="pol-card">
                                <div className="pol-card-head">
                                    <h4>{editId ? "Edit Policy" : "New Policy"}</h4>
                                    {editId && <span className="pol-edit-badge">EDIT</span>}
                                </div>

                                <div className="pol-form">
                                    <label className="pol-label">Who sees this?<span className="pol-req"> *</span></label>
                                    <Select
                                        classNamePrefix="pol-select" {...SELECT_PORTAL}
                                        options={AUDIENCE_TYPES}
                                        value={AUDIENCE_TYPES.find(a => a.value === audienceType)}
                                        onChange={(sel) => { setAudienceType(sel.value); setAudienceValue([]); }}
                                    />
                                    {isScoped && (
                                        <div className="pol-sub">
                                            <label className="pol-label">Select {audienceType.replace("_", " ")}</label>
                                            <Select
                                                key={audienceType}
                                                isMulti
                                                {...checkboxMultiProps}
                                                classNamePrefix="pol-select" {...SELECT_PORTAL}
                                                options={audienceOptionsFor(audienceType)}
                                                value={audienceOptionsFor(audienceType).filter(o => audienceValue.includes(String(o.value)))}
                                                onChange={(sel) => setAudienceValue((sel || []).map(s => String(s.value)))}
                                            />
                                        </div>
                                    )}

                                    <label className="pol-label">Title<span className="pol-req"> *</span></label>
                                    <input className="pol-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Policy title" maxLength={150} />

                                    <label className="pol-label">
                                        Excerpt <span className="pol-hint">(the one line shown on the portal card)</span>
                                        <span className="pol-count ok">{excerpt.length}/200</span>
                                    </label>
                                    <input className="pol-input" value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Short summary shown on the card…" maxLength={200} />

                                    <label className="pol-label">
                                        Description<span className="pol-req"> *</span>
                                        <span className={`pol-count ${descLen < MIN_DESC ? "short" : "ok"}`}>{descLen}/{MIN_DESC} min</span>
                                    </label>
                                    <div ref={quillRef} className="pol-editor" />

                                    <div className="pol-actions">
                                        {editId && <button className="pol-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>}
                                        <button className="pol-btn-save" onClick={submitPolicy} disabled={saving}>
                                            {saving ? "Saving…" : editId ? "Update Policy" : "Publish Policy"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── RIGHT: live portal preview (card teaser ↔ full page) ── */}
                        <div className="pol-preview-col">
                            <div className="pol-preview-label">
                                <span className="pol-live" /> Portal preview
                                <div className="pol-preview-toggle">
                                    <button type="button" className={previewMode === "card" ? "active" : ""} onClick={() => setPreviewMode("card")}>Card</button>
                                    <button type="button" className={previewMode === "full" ? "active" : ""} onClick={() => setPreviewMode("full")}>Full page</button>
                                </div>
                            </div>

                            {previewMode === "card" ? (
                                <>
                                    <p className="pol-preview-note">How it looks in the Policies list on the portal.</p>
                                    <div className="pol-teaser">
                                        <div className="pol-teaser-title">{title.trim() || "Policy title"}</div>
                                        <div className="pol-teaser-sub">{cardTeaser(excerpt, description) || "Your excerpt will appear here…"}</div>
                                        <span className="pol-teaser-more">Learn More →</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="pol-preview-note">How it looks after clicking “Learn More”.</p>
                                    <div className="pol-portal-card">
                                        <div className="pol-portal-badges">
                                            <span className="pol-badge pol-badge-aud">{audienceBadge(audienceType, audienceValue.join(","))}</span>
                                        </div>
                                        <div className="pol-portal-title">{title.trim() || "Policy title"}</div>
                                        <div className="pol-portal-desc" dangerouslySetInnerHTML={{ __html: (description && description !== "<p><br></p>") ? description : "<p style='color:#94a3b8'>Your policy text will appear here…</p>" }} />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── Published policies (drag to reorder) ── */}
                    <div className="pol-published">
                        <div className="pol-published-head">
                            Published Policies <span className="pol-count-pill">{policies.length}</span>
                            {policies.length > 1 && <span className="pol-reorder-hint">drag the handle to reorder</span>}
                        </div>

                        {policies.length === 0 ? (
                            <div className="pol-empty">
                                <div className="pol-empty-icon">📋</div>
                                <p>No policies yet - create one above.</p>
                            </div>
                        ) : (
                            <DragDropContext onDragEnd={handleReorder}>
                                <Droppable droppableId="policies">
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps} className="pol-list">
                                            {policies.map((p, index) => (
                                                <Draggable key={p.policy_id} draggableId={String(p.policy_id)} index={index}>
                                                    {(prov) => (
                                                        <div ref={prov.innerRef} {...prov.draggableProps} className="pol-item">
                                                            <span className="pol-drag" {...prov.dragHandleProps} title="Drag to reorder"><FaGripVertical /></span>
                                                            {/* teaser card - exactly what the portal list shows */}
                                                            <div className="pol-item-body">
                                                                <div className="pol-portal-badges">
                                                                    <span className="pol-badge pol-badge-aud">{audienceBadge(p.audience_type, p.audience_value)}</span>
                                                                    <span className="pol-order">#{index + 1}</span>
                                                                </div>
                                                                <div className="pol-portal-title">{p.title}</div>
                                                                <div className="pol-teaser-sub">{cardTeaser(p.excerpt, p.description)}</div>
                                                                <span className="pol-teaser-more">Learn More →</span>
                                                            </div>
                                                            <div className="pol-item-actions">
                                                                <button className="pol-icon-btn" onClick={() => startEdit(p)} title="Edit"><FaEdit /></button>
                                                                <button className="pol-icon-btn pol-icon-del" onClick={() => deletePolicy(p.policy_id)} title="Delete"><FaTrash /></button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dolluzcorp_Policies_config;
